import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ResourceFinder from '../components/ResourceFinder';
import LicensingCerts from '../components/LicensingCerts';
import CareerPayGuide from '../components/CareerPayGuide';
import TradeHub from '../components/TradeHub';
import LanguageToggle from '../components/LanguageToggle';
import useIsPhone from '../utils/useIsPhone';
import VocationWorksLogo from '../components/VocationWorksLogo';
import ResourceTabs, { RESOURCE_TAB_KEYS, DIRECTORY_CONTAINER, DirectoryHeading } from '../components/ResourceTabs';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';

// Public, no-account version of the same Resources experience found inside the worker
// dashboard (School Finder / Union Finder / Licensing & Certs). Reuses ResourceFinder and
// LicensingCerts directly, unmodified -- this is a new entry point, not a rebuild. Deliberately
// has no sign-in links, nav CTAs, or "create an account to see more" gating anywhere on it.
export default function PublicResources() {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  // ?tab=<key> preselects a tab -- the tab row on /union-wages links back here that way.
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(RESOURCE_TAB_KEYS.includes(tabParam) ? tabParam : 'school');
  // Deep-link seed for Career Pay Guide, set when a School/Union Finder pay snippet is
  // clicked -- CareerPayGuide clears this itself once applied (see onInitialTradeApplied)
  // so a later direct visit to the tab doesn't "stick" on a stale trade.
  // ?tab=pay&trade=<name> deep-links straight to that trade (the Workers page guide cards use it),
  // and ?tab=pay&quiz=1 opens the Career Pay Guide with the trade quiz already open.
  const [payGuideInitialTrade, setPayGuideInitialTrade] = useState(() => (tabParam === 'pay' && searchParams.get('trade')) || null);
  const [openQuizOnLoad] = useState(() => tabParam === 'pay' && searchParams.get('quiz') === '1');
  const goToTradePay = (tradeName) => { setActiveTab('pay'); setPayGuideInitialTrade(tradeName); };
  // Trade Hub overlay -- independent of activeTab, opened from a Licensing & Certs card or a
  // Career Pay Guide detail view. Closing it just clears this state and returns to whatever
  // tab was open underneath.
  const [activeTradeHub, setActiveTradeHub] = useState(null);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      body { background: ${NAVY}; font-family: 'DM Sans', sans-serif; margin: 0; }
      input::placeholder { color: rgba(255,255,255,0.28); }
      select option { background-color: ${NAVY_MID}; color: ${WHITE}; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: NAVY }}>
      {/* Minimal header -- logo only, links back to the homepage for anyone who wants to
          explore further on their own initiative. No sign-in link, no CTA button. */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100, height: '60px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
      }}>
        <div
          onClick={() => navigate('/')}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
        >
          <VocationWorksLogo variant="reversed" size={32} />
        </div>
        <LanguageToggle large={isPhone} />
      </nav>

      <div style={DIRECTORY_CONTAINER}>
        <DirectoryHeading />
        <ResourceTabs activeTab={activeTab} onSelect={setActiveTab} fullWidth />

        {activeTab === 'school' && (
          <ResourceFinder type="school" onViewTradePay={goToTradePay} />
        )}
        {activeTab === 'union' && (
          <ResourceFinder type="union" onViewTradePay={goToTradePay} />
        )}
        {activeTab === 'licensing' && <LicensingCerts onViewTradeHub={setActiveTradeHub} />}
        {activeTab === 'pay' && (
          <CareerPayGuide
            initialTrade={payGuideInitialTrade}
            onInitialTradeApplied={() => setPayGuideInitialTrade(null)}
            onViewTradeHub={setActiveTradeHub}
            initialQuizOpen={openQuizOnLoad}
          />
        )}
      </div>

      {activeTradeHub && (
        <TradeHub hubTrade={activeTradeHub} onClose={() => setActiveTradeHub(null)} />
      )}
    </div>
  );
}
